import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "../approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "../ClientTools";
import { createClientTool, toolError, toolSuccess } from "../ClientTools";

import { SearchTopicConfirmation } from "./confirmations";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface SearchTopicInput {
  topic_id: string;
  /** Timestamp in nanoseconds since Unix epoch. */
  timestamp?: number;
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: SearchTopicInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  if (!deps.topicService) {
    return toolError("Topic service is not available");
  }

  if (!input.topic_id) {
    return toolError("topic_id is required");
  }

  try {
    const topicService = deps.topicService;
    const topic = await topicService.getTopicById(input.topic_id);
    const timestamp =
      input.timestamp !== undefined ? BigInt(input.timestamp) : undefined;

    if (deps.isOnVisualizerPage?.()) {
      const published = deps.publishWorkspaceAction?.({
        type: "open_topic_search",
        payload: { topic, timestamp },
      });
      if (!published) {
        return toolError("Failed to publish workspace action");
      }
      return toolSuccess("search_topic", { opened_in: "current_visualizer" });
    }

    deps.goto.visualize({
      dataSource: { files: [topic.association.association_id] },
      params: { t: timestamp },
      state: {
        initialTopicFilter: {
          term: topic.topic_name,
          key: Date.now(),
        },
      },
    });

    return toolSuccess("search_topic", {
      navigated_to: Goto.route.Visualize,
    });
  } catch (err) {
    return toolError(err instanceof Error ? err.message : String(err));
  }
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<SearchTopicInput>({
  name: "client_visualize_search_topic",
  description: `Open the visualizer sidebar with a topic search filter pre-filled. Useful when the user wants to explore a topic's message paths before deciding what to plot. If the user is not already on the visualizer page, navigates there first. Optionally seeks the timeline to a specific timestamp after opening.`,
  input_schema: {
    type: "object",
    properties: {
      topic_id: {
        type: "string",
        description:
          "Topic ID (e.g., 'tp_abc123'). The topic to search for in the sidebar.",
      },
      timestamp: {
        type: "integer",
        description:
          "Timestamp in nanoseconds since Unix epoch. Optional — seeks the timer after opening.",
      },
    },
    required: ["topic_id"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Search Topic",
  displayDescription: "Opens the visualizer with a topic search filter.",
  ConfirmationContent: SearchTopicConfirmation,
});
